import React, { Component } from "react";
import { connect } from "react-redux";
import { defaultState, EjariContractDataAction, EjariContractDataState } from "../reducers/EjariContractData";
import { getEjariActiveUserId } from "@10x/sso";
import { dispatchX } from "@my-dld/utils/StoreUtils";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { IStore } from "@my-dld/reducers/store";
import DefaultLoader, { EjariCtxContractLoader } from "../loaders/ContractLoader";
import { Contract, TerminateRequest } from "@eres/ejari-api/Entity/contract";
import { RememberThis } from "@eres/forms/decoratetors";
import { buildContractForSubmission } from "../Utils";
import { EjariApi } from "@eres/ejari-api";
import { TenancyContactAction } from "../constants";
import { trackEvent, trackEventWithData } from "@my-dld/utils/Analytics";


export interface IEjariContractContext extends EjariContractDataState {
	submitContract: ( ctx: IEjariContractContext ) => Promise<Contract>;
	terminateContract: ( req: TerminateRequest, ctx: IEjariContractContext ) => Promise<{ Identity: number }>;
}


interface Props extends IEjariContractContext {
	extraContext?: Partial<EjariContractDataState>;
	loader?: EjariCtxContractLoader;
	onContractActionFinished: ( ctx: IEjariContractContext, data: Contract | { Identity: number } ) => void;
}

export const defaultCtx: IEjariContractContext = {
	...defaultState,
	submitContract: () => Promise.reject( "Not ready" ),
	terminateContract: () => Promise.reject( "Not ready" ),
};

export const EjariContractContext = React.createContext( defaultCtx );

interface State {
}

class EjariContractContextWrapper extends Component<Props, State> {
	static defaultProps: Partial<Props> = {
		loader: DefaultLoader,
	};

	constructor( props: Props ) {
		super( props );
		this.state = {
			isLoading: true
		};
	}

	@RememberThis()
	public static buildContext( cmp: EjariContractContextWrapper, props: Props ) {
		return {
			...props,
			submitContract: cmp.submitContract
		} as IEjariContractContext;
	}

	componentDidMount() {
		dispatchX( {
			type: EjariContractDataAction.EJARI_CONTRACT_UPDATE_STATE,
			payload: {
				activeUserId: getEjariActiveUserId(),
			}
		} );
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		if ( prevProps.isRefreshing != this.props.isRefreshing ) {
			if ( this.props.isRefreshing )
				showLoading();
			else hideLoading();
		}
	}

	render() {
		const { children, extraContext, loader, ...props } = this.props;
		const Loader = loader!;
		return <EjariContractContext.Provider
			value={ {
				...props,
				submitContract: this.submitContract,
				terminateContract: this.terminateContract,
			} }>
			<Loader>
				{ children }
			</Loader>
		</EjariContractContext.Provider>;
	}

	terminateContract = ( req: TerminateRequest, ctx: IEjariContractContext ) => {

		trackEventWithData( "EjariContract", "TerminateTry", { contractId: req.ContractId + "" } );
		showLoading();
		return EjariApi.terminateContract( req )
		.then( x => {
			trackEventWithData( "EjariContract", "TerminateSubmitted", { contractId: req.ContractId + "" } );
			hideLoading();
			this.props.onContractActionFinished( ctx, x );
			return Promise.resolve( x );
		} ).catch( e => {
			trackEventWithData( "EjariContract", "TerminateFailed", { contractId: req.ContractId + "" } );
			hideLoading();
			showErrorMessage( e );
			return Promise.reject( e );
		} );
	};
	submitContract = ( ctx: IEjariContractContext ) => {
		showLoading();
		const contract = buildContractForSubmission( ctx );
		let call = EjariApi.submitContract;
		let isRenew = ctx.action === TenancyContactAction.RENEW_CONTRACT;
		if ( isRenew ) {
			call = EjariApi.renewContract;
			contract.TenancyContractId = ctx.contract?.ContractId;
			trackEventWithData( "EjariContract", "RenewTry", { contractId: contract.TenancyContractId + "" } );
		} else {
			trackEvent( "EjariContract", "NewContractTry" );
		}
		return call( contract )
		.then( ( response ) => {
			hideLoading();
			if ( isRenew ) {
				trackEventWithData( "EjariContract", "RenewSubmitted", {
					OldContractId: contract.TenancyContractId + "",
					contractNumber: response.ContractNumber + "",
				} );
			} else {
				trackEventWithData( "EjariContract", "NewContractSubmitted", { contractNumber: response.ContractNumber + "" } );
			}
			this.props.onContractActionFinished( ctx, response );
			return Promise.resolve( response );
		} ).catch( ( reason ) => {
			hideLoading();
			if ( isRenew ) {
				trackEventWithData( "EjariContract", "RenewFailed", {
					contractId: contract.TenancyContractId + "",
					reason: reason.toString(),
				} );
			} else {
				trackEventWithData( "EjariContract", "NewContractFailed", { reason: reason.toString() } );
			}
			showErrorMessage( reason );
			return Promise.reject( reason );
		} );
	};
}

const mapStateToProps = ( state: IStore, ownProps: Partial<Props> & { children?: any } ): Props => {
	return {
		...state.EjariContractData,
	} as Props;
};


export default connect( mapStateToProps )( EjariContractContextWrapper );

export function updateContractContext( data: Partial<EjariContractDataState> ) {
	dispatchX( {
		type: EjariContractDataAction.EJARI_CONTRACT_UPDATE_STATE,
		payload: {
			...data
		}
	} );
}

export function resetContractContext() {
	dispatchX( {
		type: EjariContractDataAction.EJARI_CONTRACT_CLEAR_DATA,
	} );
}
